import { useState } from 'react';
import { X, Search, Loader2, Sparkles, PenLine, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const TEAM_MEMBERS = ['Zain', 'Stiofan', 'Unassigned'];
const BUSINESS_TYPES = ['Hotel', 'Gym', 'Cafe', 'Coworking', 'Restaurant', 'Shopping Centre', 'Transport', 'University', 'Other'];

interface EnrichedData {
  name: string;
  address: string;
  website: string;
  type: string;
  contact_name: string;
  contact_role: string;
  contact_email: string;
  contact_source: string;
}

interface AddProspectFormProps {
  onClose: () => void;
  onAdded: () => void;
}

const AddProspectForm = ({ onClose, onAdded }: AddProspectFormProps) => {
  const [step, setStep] = useState<'search' | 'review'>('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [contactSource, setContactSource] = useState('');

  const [form, setForm] = useState({
    name: '',
    address: '',
    website: '',
    type: '',
    contact_name: '',
    contact_role: '',
    contact_email: '',
    contact_phone: '',
    instagram: '',
    notes: '',
    assigned_to: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setSearching(true);
    try {
      const { data, error } = await supabase.functions.invoke('enrich-prospect', {
        body: { query: searchQuery.trim() },
      });

      if (error) {
        console.error('Enrichment error:', error);
        toast.error('Enrichment failed. Switching to manual entry.');
        setForm((f) => ({ ...f, name: searchQuery.trim() }));
        setStep('review');
        return;
      }

      if (data?.success && data.data) {
        const d = data.data as EnrichedData;
        setForm((f) => ({
          ...f,
          name: d.name || searchQuery.trim(),
          address: d.address || '',
          website: d.website || '',
          type: d.type || '',
          contact_name: d.contact_name || '',
          contact_role: d.contact_role || '',
          contact_email: d.contact_email || '',
        }));
        setContactSource(d.contact_source || '');
        toast.success('Found business details!');
      } else {
        toast('No results found. You can fill in details manually.');
        setForm((f) => ({ ...f, name: searchQuery.trim() }));
      }
      setStep('review');
    } catch (err) {
      console.error('Search error:', err);
      toast.error('Something went wrong. Try manual entry.');
      setForm((f) => ({ ...f, name: searchQuery.trim() }));
      setStep('review');
    } finally {
      setSearching(false);
    }
  };

  const handleManualEntry = () => {
    setForm((f) => ({ ...f, name: searchQuery.trim() }));
    setStep('review');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Location name is required');
      return;
    }
    setSaving(true);
    const { error } = await supabase.from('prospects').insert({
      name: form.name.trim(),
      address: form.address.trim(),
      website: form.website.trim() || null,
      type: form.type || null,
      contact_name: form.contact_name.trim() || null,
      contact_role: form.contact_role.trim() || null,
      contact_email: form.contact_email.trim() || null,
      contact_phone: form.contact_phone.trim() || null,
      instagram: form.instagram.trim() || null,
      notes: form.notes.trim() || null,
      assigned_to: form.assigned_to || null,
      status: 'prospect' as const,
    });
    setSaving(false);
    if (error) {
      toast.error('Failed to add prospect');
      console.error(error);
    } else {
      toast.success('Prospect added');
      onAdded();
      onClose();
    }
  };

  const field = (label: string, key: keyof typeof form, placeholder: string, type = 'text') => (
    <div>
      <label className="text-xs font-medium text-muted-foreground mb-1 block">{label}</label>
      <input
        type={type}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        placeholder={placeholder}
        className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-card rounded-xl border border-border shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="font-semibold text-foreground">
            {step === 'search' ? 'Add Prospect' : 'Review & Confirm'}
          </h3>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        {step === 'search' ? (
          <div className="p-6 space-y-4">
            <p className="text-sm text-muted-foreground">
              Search for a business and we'll auto-fill the details using AI.
            </p>

            <form onSubmit={handleSearch} className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder='e.g. "Hoxton Hotel Shoreditch" or "Third Space Soho"'
                  className="w-full pl-10 pr-4 py-3 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  autoFocus
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" disabled={searching || !searchQuery.trim()} className="flex-1">
                  {searching ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Researching...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Smart Search
                    </>
                  )}
                </Button>
                <Button type="button" variant="outline" onClick={handleManualEntry}>
                  <PenLine className="w-4 h-4 mr-1" />
                  Manual
                </Button>
              </div>
            </form>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
            <div className="p-4 flex-1 overflow-y-auto space-y-3">
              {field('Location Name *', 'name', 'e.g. The Hoxton, Holborn')}
              {field('Address', 'address', 'e.g. 199-206 High Holborn, London WC1V 7BD')}
              {field('Website', 'website', 'https://...')}

              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Type</label>
                <select
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground"
                >
                  <option value="">Select type...</option>
                  {BUSINESS_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {field('Contact Name', 'contact_name', 'e.g. Sarah Jones')}
                {field('Role', 'contact_role', 'e.g. Events Manager')}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {field('Email', 'contact_email', 'email@example.com', 'email')}
                {field('Phone', 'contact_phone', '+44...')}
              </div>
              {field('Instagram', 'instagram', '@handle or URL')}

              {/* Contact Source */}
              {contactSource && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex gap-2">
                  <Info className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-blue-700 mb-0.5">Contact Source</p>
                    <p className="text-xs text-blue-600">{contactSource}</p>
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Assigned To</label>
                <select
                  value={form.assigned_to}
                  onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground"
                >
                  <option value="">Select...</option>
                  {TEAM_MEMBERS.filter((m) => m !== 'Unassigned').map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Notes</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                  placeholder="Any initial notes..."
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground resize-y"
                />
              </div>
            </div>

            <div className="p-4 border-t border-border flex justify-between">
              <Button type="button" variant="ghost" onClick={() => setStep('search')}>
                ← Back
              </Button>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                <Button type="submit" disabled={saving}>
                  {saving ? 'Adding...' : 'Add Prospect'}
                </Button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AddProspectForm;
